import React, { useContext } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { Controller, useWatch } from 'react-hook-form';
import { Button, FormControlLabel, Checkbox } from '@mui/material';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';
import { FormError } from 'ui/components/elements/FormError/FormError';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { useValuationEstimateForm } from './useValuationEstimateForm';
import { mqMin } from 'utils/responsive';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { ValuationEstimateStorageRepository } from 'interfaceAdapter/repositories/ValuationEstimateStorage';

const ContentWrapper = styled.div`
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
`;

const StyledButton = styled(Button)`
    margin-top: 16px;
    background-color: var(--dark-blue);
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
    padding: 12px 0;
    border-radius: 4px;
    text-transform: none;

    &:hover {
        background-color: var(--dark-blue);
        opacity: 0.9;
    }

    &:disabled {
        background-color: #ccc;
    }
`;

const Description = styled.div`
    font-size: 1rem;
    color: var(--dark-blue);
    margin-bottom: 24px;
    text-align: center;

    ${mqMin('SP')} {
        font-size: 1.2rem;
    }
`;

const PriceRangeTitle = styled.h3`
    font-size: 1.2rem;
    color: var(--dark-blue);
    text-align: center;
    font-weight: 700;
    margin-top: 20px;
`;

const PriceRangeDisplay = styled.div`
    text-align: center;
    color: var(--dark-blue);
    font-size: 1.2rem;
    font-weight: 600;
    line-height: 1.8;

    strong {
        font-size: 2rem;
        font-weight: 900;
        color: var(--dark-blue);
        margin-right: 2px;
    }
`;

const PriceValue = styled.span`
    color: var(--dark-blue);
`;

const PriceSeparator = styled.span`
    color: var(--dark-blue);
    margin: 0 4px;
`;

const RegisterButton = styled(Button)`
    margin-top: 32px;
    background-color: var(--main-color-blue);
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
    padding: 16px 0;
    border-radius: 50px;
    text-transform: none;
    width: 100%;

    &:hover {
        background-color: var(--main-color-blue);
        opacity: 0.9;
    }

    ${mqMin('SP')} {
        font-size: 1.3rem;
    }
`;

const ipRightHolderOptions = [
    { value: 'corporation', label: '法人' },
    { value: 'individual', label: '個人' },
];

export const ValuationEstimateForm: React.FC = () => {
    const navigate = useNavigate();
    const diContainer = useContext(diContainerContext);
    const valuationEstimateStorageRepository = diContainer.resolve(ValuationEstimateStorageRepository);
    const { control, errors, submit, loading, complete, priceRange, getValues } = useValuationEstimateForm();
    const hasIntellectualProperty = useWatch({
        control,
        name: 'has_intellectual_property',
    });

    const handleRegister = () => {
        const formData = getValues();
        valuationEstimateStorageRepository.set({
            revenue_mjpy: formData.revenue_mjpy as number,
            operating_income_mjpy: formData.operating_income_mjpy as number,
            depreciation_mjpy: formData.depreciation_mjpy as number,
            cash_and_deposits_mjpy: formData.cash_and_deposits_mjpy as number,
            interest_bearing_debt_mjpy: formData.interest_bearing_debt_mjpy as number,
            net_assets_mjpy: formData.net_assets_mjpy as number,
            has_intellectual_property: formData.has_intellectual_property,
            ip_right_holder_type: formData.ip_right_holder_type || null,
        });
        navigate('/send-registration-mail/');
    };

    return (
        <>
            {complete ? (
                <ContentWrapper>
                    {priceRange && (
                        <>
                            <PriceRangeTitle>貴社の場合・・・</PriceRangeTitle>
                            <PriceRangeDisplay>
                                <strong>
                                    <PriceValue>
                                        {priceRange.lower.toLocaleString('ja-JP', {
                                            maximumFractionDigits: 0,
                                        })}
                                    </PriceValue>
                                    <PriceSeparator>～</PriceSeparator>
                                    <PriceValue>
                                        {priceRange.upper.toLocaleString('ja-JP', {
                                            maximumFractionDigits: 0,
                                        })}
                                    </PriceValue>
                                    百万円
                                </strong>
                                <br />
                                ほどとなる可能性があります！
                            </PriceRangeDisplay>
                            <RegisterButton variant="contained" onClick={handleRegister}>
                                登録してM&Aパートナーを探す
                            </RegisterButton>
                        </>
                    )}
                </ContentWrapper>
            ) : (
                <ContentWrapper>
                    <Description>貴社の財務情報を入力してください</Description>
                    <Form onSubmit={submit}>
                        <FlexContainer>
                            <FormLabel title="売上高（百万円）" isRequired />
                            <Controller
                                name="revenue_mjpy"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput {...field} type="text" hasError={!!errors.revenue_mjpy?.message} />
                                        {errors.revenue_mjpy?.message && (
                                            <FormError message={errors.revenue_mjpy.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>

                        <FlexContainer>
                            <FormLabel title="営業利益（百万円）" isRequired />
                            <Controller
                                name="operating_income_mjpy"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput
                                            {...field}
                                            type="text"
                                            hasError={!!errors.operating_income_mjpy?.message}
                                        />
                                        {errors.operating_income_mjpy?.message && (
                                            <FormError message={errors.operating_income_mjpy.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>

                        <FlexContainer>
                            <FormLabel title="減価償却費（百万円）" isRequired />
                            <Controller
                                name="depreciation_mjpy"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput
                                            {...field}
                                            type="text"
                                            hasError={!!errors.depreciation_mjpy?.message}
                                        />
                                        {errors.depreciation_mjpy?.message && (
                                            <FormError message={errors.depreciation_mjpy.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>

                        <FlexContainer>
                            <FormLabel title="現預金（百万円）" isRequired />
                            <Controller
                                name="cash_and_deposits_mjpy"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput
                                            {...field}
                                            type="text"
                                            hasError={!!errors.cash_and_deposits_mjpy?.message}
                                        />
                                        {errors.cash_and_deposits_mjpy?.message && (
                                            <FormError message={errors.cash_and_deposits_mjpy.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>

                        <FlexContainer>
                            <FormLabel title="有利子負債（百万円）" isRequired />
                            <Controller
                                name="interest_bearing_debt_mjpy"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput
                                            {...field}
                                            type="text"
                                            hasError={!!errors.interest_bearing_debt_mjpy?.message}
                                        />
                                        {errors.interest_bearing_debt_mjpy?.message && (
                                            <FormError message={errors.interest_bearing_debt_mjpy.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>

                        <FlexContainer>
                            <FormLabel title="純資産（百万円）" isRequired />
                            <Controller
                                name="net_assets_mjpy"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput
                                            {...field}
                                            type="text"
                                            hasError={!!errors.net_assets_mjpy?.message}
                                        />
                                        {errors.net_assets_mjpy?.message && (
                                            <FormError message={errors.net_assets_mjpy.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>

                        <FlexContainer>
                            <FormLabel title="知的財産の有無" />
                            <Controller
                                name="has_intellectual_property"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Checkbox {...field} checked={field.value} />}
                                        label="知的財産を保有している"
                                    />
                                )}
                            />
                        </FlexContainer>

                        <FlexContainer>
                            <FormLabel title="知的財産権利者種別" />
                            <Controller
                                name="ip_right_holder_type"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormSelect
                                            {...field}
                                            options={ipRightHolderOptions}
                                            hasError={!!errors.ip_right_holder_type?.message}
                                            disabled={!hasIntellectualProperty}
                                            placeholder="選択してください"
                                        />
                                        {errors.ip_right_holder_type?.message && (
                                            <FormError message={errors.ip_right_holder_type.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>

                        <StyledButton type="submit" variant="contained" disabled={loading}>
                            {loading ? '計算中...' : '企業価値を計算する'}
                        </StyledButton>
                    </Form>
                </ContentWrapper>
            )}
        </>
    );
};
